import React, { useEffect, useState } from "react";
import { Button, Card, Row, Col, Table, Badge, Form, Alert, Modal } from "react-bootstrap";
import {
    getPropertiesCount,
    chatbotLeadsCount,
    contactLeadsCount,
    mortgageLeadsCount,
    getAdvertisement,
    createAdvertisement,
    getSEOSettings,
    createSEOSettings,
    getAllSEOSettings,
} from "../../actions/commonController";
import { toast } from "react-toastify";

const Overview = () => {
    const [loader, setLoader] = useState(true);
    const [properties, setProperties] = useState({
        total: 0,
        byType: {
            'Offplan': 0,
            'Rent': 0,
            'Mortgage': 0,
            'Sale': 0,
            'Lease': 0,
            'Ready Plan': 0,
            'Sold Out': 0
        }
    });

    const [leads, setLeads] = useState({
        total: 0,
        newThisWeek: 0,
        bySource: {
            'Chatbot': 0,
            'Mortgage Calculator': 0,
            'Contact Form': 0,
        },
        recentLeads: []
    });

    const defaultMediaSettings = {
        desktop: { type: 'image', file: null },
        tablet: { type: 'video', file: null },
        mobile: { type: 'video', file: null }
    };

    const defaultAdData = {
        heroTitle: "DAMAC Islands Phase 2 Launch",
        heroSubtitle: "Luxury 4-7 Bedroom Residences in Dubai's Only Island Community. Discover Sandy Beaches & Crystal Lagoons",
        startingPrice: "2,750,000",
        paymentPlan: "Announcing Soon",
        brochureFile: null,
        currentHeroImage: "HeaderImage.jpg",
        currentHeroVideo: "HeaderImage.mp4",
        mediaSettings: defaultMediaSettings
    };

    const defaultSEOData = {
        websiteTitle: "WizHull Properties | DAMAC Island Phase 2",
        websiteDescription: "Explore DAMAC Islands Phase 2 - Dubai's premier island community featuring luxury 4-7 bedroom residences with private beaches and crystal-clear lagoons.",
        websiteKeywords: "mortgage, homeownership, financial solutions, mortgage rates, mortgage process"
    };

    // Advertisement & SEO state
    const [adData, setAdData] = useState(defaultAdData);
    const [seoData, setSeoData] = useState(defaultSEOData);
    const [allAdvertisements, setAllAdvertisements] = useState([]);
    const [allSEOSettings, setAllSEOSettings] = useState([]);
    const [activeAdvertisement, setActiveAdvertisement] = useState(null);
    const [activeSEOSettings, setActiveSEOSettings] = useState(null);

    const [activeHeroAds, setActiveHeroAds] = useState([]);
    const [heroAdsLoading, setHeroAdsLoading] = useState(true);

    const [imagePreview, setImagePreview] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [adLoading, setAdLoading] = useState(false);
    const [seoLoading, setSeoLoading] = useState(false);
    const [showHeroModal, setShowHeroModal] = useState(false);
    const [showSEOModal, setShowSEOModal] = useState(false);
    const [showAdsHistoryModal, setShowAdsHistoryModal] = useState(false);
    const [showSEOHistoryModal, setShowSEOHistoryModal] = useState(false);

    const propertyTypeColors = {
        'Offplan': 'primary',
        'Rent': 'info',
        'Mortgage': 'warning',
        'Sale': 'success',
        'Lease': 'purple',
        'Ready Plan': 'orange',
        'Sold Out': 'secondary'
    };

    const propertyTypeIcons = {
        'Offplan': 'ri-building-line',
        'Rent': 'bi bi-wallet2',
        'Mortgage': 'ri-bank-line',
        'Sale': 'bi bi-receipt',
        'Lease': 'bi bi-hand-thumbs-up',
        'Ready Plan': 'ri-home-gear-line',
        'Sold Out': 'bi bi-bag-x-fill'
    };

    const leadSourceColors = {
        'Chatbot': 'primary',
        'Mortgage Calculator': 'success',
        'Contact Form': 'info'
    };

    // ---------- LOADERS ----------
    const loadData = async () => {
        try {
            setLoader(true);
            const [propertiesRes, chatbotRes, contactRes, mortgageRes] = await Promise.all([
                getPropertiesCount(),
                chatbotLeadsCount(),
                contactLeadsCount(),
                mortgageLeadsCount()
            ]);

            if (propertiesRes.status === "success") setProperties(propertiesRes.data);

            const chatbotCount = chatbotRes.status === "success" ? chatbotRes.data : 0;
            const contactCount = contactRes.status === "success" ? contactRes.data : 0;
            const mortgageCount = mortgageRes.status === "success" ? mortgageRes.data : 0;
            const totalLeads = chatbotCount + contactCount + mortgageCount;

            setLeads({
                total: totalLeads,
                newThisWeek: 0,
                bySource: {
                    'Chatbot': chatbotCount,
                    'Mortgage Calculator': mortgageCount,
                    'Contact Form': contactCount
                },
                recentLeads: []
            });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoader(false);
        }
    };

    const loadActiveHeroAdvertisements = async () => {
        try {
            setHeroAdsLoading(true);
            const { status, data } = await getAdvertisement();
            if (status === "success" && Array.isArray(data)) {
                setActiveHeroAds(data); // max 3
            }
        } catch (error) {
            console.error("Failed to load active hero ads", error);
        } finally {
            setHeroAdsLoading(false);
        }
    };

    const loadAdvertisementData = async () => {
        try {
            const { status, data } = await getAdvertisement(); // get latest active ad for form
            if (status === "success" && Array.isArray(data) && data.length > 0) {
                setAdData(data[0]); // single active for editing
                setActiveAdvertisement(data[0]);
            } else {
                setAdData(defaultAdData);
            }
        } catch (error) {
            console.error('Error loading advertisement data:', error);
            setAdData(defaultAdData);
        }
    };

    const loadSEOSettings = async () => {
        try {
            const { status, data } = await getSEOSettings();
            if (status === "success" && data) {
                setSeoData(data);
                setActiveSEOSettings(data);
                document.title = data.websiteTitle ? `${data.websiteTitle} - Admin` : "Admin Dashboard";
            } else {
                setSeoData(defaultSEOData);
                document.title = defaultSEOData.websiteTitle ? `${defaultSEOData.websiteTitle} - Admin` : "Admin Dashboard";
            }
        } catch (error) {
            console.error('Error loading SEO settings:', error);
            setSeoData(defaultSEOData);
        }
    };

    const loadAllSEOSettings = async () => {
        try {
            const { status, data } = await getAllSEOSettings();
            if (status === "success") setAllSEOSettings(data);
        } catch (error) {
            console.error('Error loading all SEO settings:', error);
            toast.error("Failed to load SEO history");
        }
    };

    useEffect(() => {
        loadData();
        loadActiveHeroAdvertisements();
        loadAdvertisementData();
        loadSEOSettings();
    }, []);

    // ---------- HELPERS ----------
    const getPercentage = (count, total) => total > 0 ? Math.round((count / total) * 100) : 0;
    const formatNumber = (num) => num?.toLocaleString() || '0';
    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    const getMediaSetting = (device, property) => adData.mediaSettings?.[device]?.[property] || defaultMediaSettings[device][property];

    // ---------- ADMIN FORM HANDLERS ----------
    const handleAdInputChange = (e) => {
        const { name, value } = e.target;
        setAdData(prev => ({ ...prev, [name]: value }));
    };

    const handleSEOInputChange = (e) => {
        const { name, value } = e.target;
        setSeoData(prev => ({ ...prev, [name]: value }));
    };

    const handleMediaTypeChange = (device, mediaType) => {
        setAdData(prev => ({
            ...prev,
            mediaSettings: { ...prev.mediaSettings, [device]: { ...prev.mediaSettings[device], type: mediaType, file: null } }
        }));
        if (device === 'desktop') {
            mediaType === 'image' ? setVideoPreview(null) : setImagePreview(null);
        }
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (!files || !files[0]) return;
        const file = files[0];

        setAdData(prev => {
            const newData = { ...prev };
            if (name.includes('desktop')) {
                const type = name.includes('Image') ? 'image' : 'video';
                newData.mediaSettings.desktop = { type, file };
                if (type === 'image') setImagePreview(URL.createObjectURL(file));
                else setVideoPreview(URL.createObjectURL(file));
            } else if (name.includes('tablet')) {
                const type = name.includes('Image') ? 'image' : 'video';
                newData.mediaSettings.tablet = { type, file };
            } else if (name.includes('mobile')) {
                const type = name.includes('Image') ? 'image' : 'video';
                newData.mediaSettings.mobile = { type, file };
            } else if (name === 'brochureFile') {
                newData.brochureFile = file;
            }
            return newData;
        });
    };

    const handleAdSubmit = async (e) => {
        e.preventDefault();
        setAdLoading(true);
        try {
            const formData = new FormData();
            formData.append('heroTitle', adData.heroTitle);
            formData.append('heroSubtitle', adData.heroSubtitle);
            formData.append('startingPrice', adData.startingPrice);
            formData.append('paymentPlan', adData.paymentPlan);

            if (adData.mediaSettings.desktop?.file) {
                const fieldName = getMediaSetting('desktop', 'type') === 'image' ? 'desktopImage' : 'desktopVideo';
                formData.append(fieldName, adData.mediaSettings.desktop.file);
            }
            if (adData.mediaSettings.tablet?.file) {
                const fieldName = getMediaSetting('tablet', 'type') === 'image' ? 'tabletImage' : 'tabletVideo';
                formData.append(fieldName, adData.mediaSettings.tablet.file);
            }
            if (adData.mediaSettings.mobile?.file) {
                const fieldName = getMediaSetting('mobile', 'type') === 'image' ? 'mobileImage' : 'mobileVideo';
                formData.append(fieldName, adData.mediaSettings.mobile.file);
            }
            if (adData.brochureFile) formData.append('brochureFile', adData.brochureFile);

            const { status } = await createAdvertisement(formData);
            if (status === "success") {
                toast.success("Advertisement created successfully!");
                loadAdvertisementData();
                loadActiveHeroAdvertisements();
                setShowHeroModal(false);
            }
        } catch (error) { console.error(error); } finally { setAdLoading(false); }
    };

    const handleSEOSubmit = async (e) => {
        e.preventDefault();
        setSeoLoading(true);
        try {
            const { status, error } = await createSEOSettings(seoData);
            if (status === "success") {
                toast.success("SEO settings saved successfully!");
                setShowSEOModal(false);
                loadSEOSettings();
                loadAllSEOSettings();
            } else toast.error(error || "Failed to save SEO settings");
        } catch (error) { console.error(error); toast.error("Failed to save SEO settings"); } finally { setSeoLoading(false); }
    };

    // ---------- DASHBOARD HERO PREVIEW ----------
    const renderHeroAdsPreview = () => {
        if (heroAdsLoading) return <Col xs={12} className="text-center py-5 text-muted">Loading hero advertisements...</Col>;
        if (!activeHeroAds.length) return <Col xs={12}><Card className="border text-center"><Card.Body className="py-5 text-muted">No active hero advertisements</Card.Body></Card></Col>;

        return activeHeroAds.map((ad, index) => (
            <Col key={ad.id} xs={12} md={activeHeroAds.length === 1 ? 12 : 6} lg={activeHeroAds.length === 3 ? 4 : 6}>
                <Card className="border h-100">
                    <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">Hero Ad {index + 1}</h6>
                        <Badge bg="primary" className="pulse-live">Live</Badge>
                    </Card.Header>
                    <Card.Body>
                        <h5 className="text-primary">{ad.heroTitle}</h5>
                        <p className="text-muted small mb-2">{ad.heroSubtitle}</p>
                        <div className="d-flex flex-wrap gap-3">
                            <small><strong>Price:</strong> AED {ad.startingPrice}</small>
                            <small><strong>Plan:</strong> {ad.paymentPlan}</small>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        ));
    };

    return (
        <div className="container-fluid">
            <div className="row mb-4">
                <div className="col-12">
                    <h4 className="page-title">Dashboard Overview</h4>
                </div>
            </div>

            {/* Active Hero Ads */}
            <Row className="g-3 mb-4">
                {renderHeroAdsPreview()}
            </Row>

            {/* ---------- SEO Section ---------- */}
            <div className="row mb-4">
                <div className="col-12">
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">SEO Settings</h5>
                            <Badge bg="primary" className="pulse-live">Live</Badge>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-2">
                                <h5 className="text-primary">Website Title:</h5>
                                <p className="mb-0">{seoData.websiteTitle}</p>
                            </div>
                            <div className="mb-2">
                                <h5 className="text-primary">Website Description:</h5>
                                <p className="mb-0">{seoData.websiteDescription}</p>
                            </div>
                            <div className="mb-2">
                                <h5 className="text-primary">Website Keywords:</h5>
                                <p className="mb-0">{seoData.websiteKeywords}</p>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/* Quick Stats Row */}
            <div className="row mb-4">
                <div className="col-xl-3 col-md-6 mb-3">
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <div className="d-flex justify-content-between">
                                <div>
                                    <h6 className="text-muted mb-1">Total Properties</h6>
                                    <h3 className="mb-0">{formatNumber(properties.total)}</h3>
                                </div>
                                <div className="align-self-center">
                                    <i className="ri-home-7-fill text-primary" style={{ fontSize: '2.5rem' }}></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                <div className="col-xl-3 col-md-6 mb-3">
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <div className="d-flex justify-content-between">
                                <div>
                                    <h6 className="text-muted mb-1">Total Leads</h6>
                                    <h3 className="mb-0">{formatNumber(leads.total)}</h3>
                                </div>
                                <div className="align-self-center">
                                    <i className="ri-user-follow-fill text-success" style={{ fontSize: '2.5rem' }}></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/* Property Distribution */}
            <div className="row mb-4">
                <div className="col-xl-6 mb-4">
                    <Card className="h-100 border-0 shadow-sm">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="card-title mb-0">Property Distribution</h5>
                                <Badge bg="primary">{properties.total} Total</Badge>
                            </div>
                            {Object.entries(properties.byType || {}).map(([type, count]) => (
                                <div key={type} className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <div className="d-flex align-items-center">
                                            <i className={`${propertyTypeIcons[type]} text-${propertyTypeColors[type]} me-2`}></i>
                                            <span className="fw-medium">{type}</span>
                                        </div>
                                        <div className="text-end">
                                            <span className="fw-bold">{count}</span>
                                            <small className="text-muted ms-1">({getPercentage(count, properties.total)}%)</small>
                                        </div>
                                    </div>
                                    <div className="progress" style={{ height: '6px' }}>
                                        <div className={`progress-bar bg-${propertyTypeColors[type]}`} style={{ width: `${getPercentage(count, properties.total)}%` }}></div>
                                    </div>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                </div>

                {/* Lead Sources */}
                <div className="col-xl-6 mb-4">
                    <Card className="h-100 border-0 shadow-sm">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="card-title mb-0">Lead Sources</h5>
                                <Badge bg="success">{leads.total} Total</Badge>
                            </div>
                            {Object.entries(leads.bySource || {}).map(([source, count]) => (
                                <div key={source} className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <div className="d-flex align-items-center">
                                            <i className={`ri-${source === 'Chatbot' ? 'chat-1-line' : source === 'Contact Form' ? 'mail-line' : 'calculator-line'} text-${leadSourceColors[source]} me-2`}></i>
                                            <span className="fw-medium">{source}</span>
                                        </div>
                                        <div className="text-end">
                                            <span className="fw-bold">{count}</span>
                                            <small className="text-muted ms-1">({getPercentage(count, leads.total)}%)</small>
                                        </div>
                                    </div>
                                    <div className="progress" style={{ height: '6px' }}>
                                        <div className={`progress-bar bg-${leadSourceColors[source]}`} style={{ width: `${getPercentage(count, leads.total)}%` }}></div>
                                    </div>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Overview